import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { PackDefinition, PackId, Rarity } from "../types";

interface PackShopProps {
  packs: PackDefinition[];
  coins: number;
  onBuyPack: (packId: PackId) => void;
  loading: boolean;
}

const RARITY_LABEL: Record<Rarity, string> = {
  common: "Common",
  uncommon: "Uncommon",
  rare: "Rare",
  epic: "Epic",
  legendary: "Legendary",
};

const RARITY_BADGE_CLASS: Record<Rarity, string> = {
  common: "",
  uncommon: "bg-emerald-600 text-white hover:bg-emerald-600",
  rare: "bg-blue-600 text-white hover:bg-blue-600",
  epic: "bg-purple-600 text-white hover:bg-purple-600",
  legendary: "bg-gradient-to-r from-yellow-500 to-amber-500 text-black",
};

const PACK_FRAME_CLASS: Record<PackId, string> = {
  standard: "border-muted bg-muted/30",
  boosted: "border-blue-400/60 bg-gradient-to-b from-blue-500/15 via-muted/20 to-muted/30",
  elite: "border-yellow-400/70 bg-gradient-to-b from-amber-500/20 via-yellow-500/10 to-muted/30",
  mythic: "border-rose-400/70 bg-gradient-to-b from-rose-500/20 via-orange-500/10 to-muted/30",
};

const PACK_ORDER: Rarity[] = ["common", "uncommon", "rare", "epic", "legendary"];

export function PackShop({ packs, coins, onBuyPack, loading }: PackShopProps) {
  const shopPacks = [...packs].sort((a, b) => a.cost - b.cost);

  return (
    <div className="flex flex-col items-center gap-8 py-10 px-4 max-w-6xl mx-auto">
      <div className="text-center space-y-1">
        <h2 className="text-3xl font-bold tracking-tight">Pack Shop</h2>
        <p className="text-muted-foreground">
          Spend more for better rarity odds.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 w-full">
        {shopPacks.map((pack) => {
          const canAfford = coins >= pack.cost;
          const shortfall = Math.max(0, pack.cost - coins);
          const visibleRarities = PACK_ORDER.filter(
            (rarity) => pack.rarityWeights[rarity] > 0
          );
          return (
            <Card key={pack.id} className="w-full">
              <CardHeader className="text-center">
                <CardTitle>{pack.name}</CardTitle>
                <CardDescription>{pack.description}</CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <div
                  className={`mx-auto w-44 h-56 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 ${PACK_FRAME_CLASS[pack.id]}`}
                >
                  <div className="text-4xl font-bold text-muted-foreground/40">
                    MOG
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {pack.cardsPerPack} cards inside
                  </p>
                  <Badge variant="outline" className="text-[10px]">
                    {pack.cost.toLocaleString()} coins
                  </Badge>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground text-center uppercase tracking-wider">
                    Drop Rates
                  </p>
                  <div className="flex flex-wrap justify-center gap-1.5">
                    {visibleRarities.map((rarity) => (
                      <Badge
                        key={`${pack.id}-${rarity}`}
                        variant={rarity === "common" ? "secondary" : "default"}
                        className={`text-[10px] gap-1 ${RARITY_BADGE_CLASS[rarity]}`}
                      >
                        {RARITY_LABEL[rarity]}
                        <span className="opacity-70">{pack.rarityWeights[rarity]}%</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>

              <CardFooter className="flex flex-col gap-2">
                <Button
                  onClick={() => onBuyPack(pack.id)}
                  disabled={!canAfford || loading}
                  className="w-full"
                  size="lg"
                >
                  {loading
                    ? "Opening..."
                    : `Open Pack \u2022 ${pack.cost.toLocaleString()} coins`}
                </Button>

                {!canAfford && !loading && (
                  <p className="text-sm text-destructive">
                    Need {shortfall.toLocaleString()} more coins
                  </p>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
